'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname, useSearchParams } from 'next/navigation';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { parseStringList, toggleArrayItem } from '../utils';
type ParamsMutator = (params: URLSearchParams) => void;
interface UrlFilterController {
  params: URLSearchParams;
  mutate: (mutator: ParamsMutator) => void;
}
const UrlFilterControllerContext = createContext<UrlFilterController | null>(null);

/**
 * 라우트 레벨 URL 필터 컨트롤러.
 * URL을 한 번만 구독하고, 같은 tick에 들어온 여러 mutate를 하나의 URLSearchParams에
 * 누적 적용한 뒤 1회만 replace한다. 여러 필터 훅이 동시에 토글해도 키가 서로를 덮어쓰지 않는다.
 *
 * router.replace는 useSearchParams를 동기 갱신하지 않으므로, 네비게이션이 착지하기 전
 * 다음 토글이 stale한 라이브 URL을 베이스로 잡으면 직전 선택이 유실된다(tick 분리 레이스).
 * 이를 막기 위해 쓰기 베이스를 "마지막으로 write한 search string"으로 누산하고,
 * 라이브 URL이 그 값과 일치하면(착지) in-flight를 해제한다. 읽기(values/count)는 항상 라이브 URL에서 파생해
 * 뒤로가기 등 외부 변경의 정확성을 유지한다. URLSearchParams 비교는 회피하고 문자열만 비교한다.
 */
export function UrlFilterControllerProvider(t0) {
  const $ = _c(22);
  const {
    children
  } = t0;
  const router = useCustomRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let paramsString;
  let t1;
  if ($[0] !== searchParams) {
    paramsString = searchParams?.toString() ?? "";
    t1 = new URLSearchParams(paramsString);
    $[0] = searchParams;
    $[1] = paramsString;
    $[2] = t1;
  } else {
    paramsString = $[1];
    t1 = $[2];
  }
  const params = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const queueRef = useRef(t2);
  const lastWrittenRef = useRef(null);
  const inFlightRef = useRef(false);
  const [flushTick, setFlushTick] = useState(0);
  let t3;
  let t4;
  if ($[4] !== paramsString) {
    t3 = () => {
      if (paramsString === lastWrittenRef.current) {
        inFlightRef.current = false;
        return;
      }
      if (!inFlightRef.current) {
        lastWrittenRef.current = paramsString;
      }
    };
    t4 = [paramsString];
    $[4] = paramsString;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== paramsString || $[8] !== pathname || $[9] !== router) {
    t5 = () => {
      const queue = queueRef.current;
      if (queue.length < 1) {
        return;
      }
      queueRef.current = [];
      const baseString = inFlightRef.current && lastWrittenRef.current !== null ? lastWrittenRef.current : paramsString;
      const nextParams = new URLSearchParams(baseString);
      queue.forEach(mutator => mutator(nextParams));
      nextParams.delete("page");
      const queryString = nextParams.toString();
      lastWrittenRef.current = queryString;
      inFlightRef.current = true;
      const url = queryString ? `${pathname}?${queryString}` : pathname;
      router.replace(url, {
        scroll: false
      });
    };
    $[7] = paramsString;
    $[8] = pathname;
    $[9] = router;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== flushTick || $[12] !== paramsString || $[13] !== pathname || $[14] !== router) {
    t6 = [flushTick, pathname, paramsString, router];
    $[11] = flushTick;
    $[12] = paramsString;
    $[13] = pathname;
    $[14] = router;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = mutator_0 => {
      queueRef.current.push(mutator_0);
      setFlushTick(_temp);
    };
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const mutate = t7;
  let t8;
  if ($[17] !== params) {
    t8 = {
      params,
      mutate
    };
    $[17] = params;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== children || $[20] !== t8) {
    t9 = <UrlFilterControllerContext.Provider value={t8}>{children}</UrlFilterControllerContext.Provider>;
    $[19] = children;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
function _temp(tick) {
  return tick + 1;
}
function useUrlFilterController() {
  const controller = useContext(UrlFilterControllerContext);
  if (!controller) {
    throw new Error("useUrlFilterController\uB294 UrlFilterControllerProvider \uB0B4\uBD80\uC5D0\uC11C\uB9CC \uC0AC\uC6A9\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.");
  }
  return controller;
}
export interface UrlMultiSelect {
  values: string[];
  toggle: (value: string) => void;
  clear: () => void;
}

/** 쉼표 구분 다중 선택 URL 파라미터를 읽고 토글/초기화하는 훅. */
export function useUrlMultiSelect(key) {
  const $ = _c(13);
  const {
    params,
    mutate
  } = useUrlFilterController();
  let t0;
  if ($[0] !== key || $[1] !== params) {
    t0 = parseStringList(params.get(key));
    $[0] = key;
    $[1] = params;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const values = t0;
  let t1;
  if ($[3] !== key || $[4] !== mutate) {
    t1 = value => {
      mutate(nextParams => {
        const current = parseStringList(nextParams.get(key));
        const next = toggleArrayItem(current, value);
        if (next.length > 0) {
          nextParams.set(key, next.join(","));
        } else {
          nextParams.delete(key);
        }
      });
    };
    $[3] = key;
    $[4] = mutate;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const toggle = t1;
  let t2;
  if ($[6] !== key || $[7] !== mutate) {
    t2 = () => {
      mutate(nextParams_0 => {
        nextParams_0.delete(key);
      });
    };
    $[6] = key;
    $[7] = mutate;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const clear = t2;
  let t3;
  if ($[9] !== clear || $[10] !== toggle || $[11] !== values) {
    t3 = {
      values,
      toggle,
      clear
    };
    $[9] = clear;
    $[10] = toggle;
    $[11] = values;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
const CATEGORY_FILTER_KEY: Record<string, string> = {
  'policy-info': 'guideTags'
};

/** 카테고리의 활성 필터 선택 개수를 파생한다. */
export function useCategoryFilterCount(category) {
  const $ = _c(3);
  const {
    params
  } = useUrlFilterController();
  const key = CATEGORY_FILTER_KEY[category];
  let t0;
  if ($[0] !== key || $[1] !== params) {
    t0 = key ? parseStringList(params.get(key)).length : 0;
    $[0] = key;
    $[1] = params;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}